import React, { useEffect, useState } from 'react';
import { Box, Button, Typography, Dialog, DialogTitle, DialogContent, DialogActions, IconButton, CircularProgress, FormControlLabel, Checkbox } from '@mui/material';
import { Close } from '@mui/icons-material';
import { toast } from 'react-toastify';
import { occasionsService } from '../../services/occasionsService';
import { vendorOccasionsService } from '../../services/vendorOccasionsService';
import { vendorProfileService } from '../../services/vendorProfileService';

interface OccasionsEditPopupProps {
  open: boolean;
  onClose: () => void;
  vendorId: string;
  onSaved: (selectedIds: string[]) => void;
}

const OccasionsEditPopup: React.FC<OccasionsEditPopupProps> = ({ open, onClose, vendorId, onSaved }) => {
  const [occasions, setOccasions] = useState<Array<{ occasionId: string; title: string }>>([]);
  const [selected, setSelected] = useState<string[]>([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    const load = async () => {
      if (!open) return;
      setLoading(true);
      try {
        const [catRes, selRes] = await Promise.all([
          occasionsService.getAllOccasions(),
          vendorOccasionsService.getVendorOccasions(vendorId)
        ]);
        setOccasions((catRes.data.occasions || []).map(o => ({ occasionId: o.occasionId, title: o.title })));
        setSelected((selRes.data.occasions || []).map(o => o.occasionId));
      } catch (e) {
        toast.error('Failed to load occasions');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [open, vendorId]);

  const toggle = (id: string) => setSelected(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);

  const handleSave = async () => {
    if (selected.length === 0) {
      toast.error('Please select at least one occasion');
      return;
    }
    setSaving(true);
    try {
      const resp = await vendorProfileService.editProfile(vendorId, { occasionsIds: selected });
      if (resp.success) {
        toast.success('Occasions updated');
        onSaved(selected);
        onClose();
      } else {
        toast.error(resp.error || 'Failed to save occasions');
      }
    } catch (e: any) {
      const msg = e?.response?.data?.error || e?.response?.data?.message || 'Failed to save occasions';
      toast.error(msg);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: '16px' } }}>
      <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', pb: 1 }}>
        <Typography sx={{ fontWeight: 700, fontSize: '20px', color: '#181818' }}>Edit Occasions</Typography>
        <IconButton onClick={onClose} disabled={saving}><Close /></IconButton>
      </DialogTitle>
      <DialogContent>
        {loading ? (
          <Box sx={{ py: 2, display: 'flex', justifyContent: 'center' }}>
            <CircularProgress />
          </Box>
        ) : (
          <Box sx={{ py: 2, display: 'flex', flexDirection: 'column', gap: 1, maxHeight: '320px', overflowY: 'auto', pr: 1 }}>
            {occasions.map(o => (
              <FormControlLabel key={o.occasionId} control={<Checkbox checked={selected.includes(o.occasionId)} onChange={() => toggle(o.occasionId)} />} label={o.title} />
            ))}
          </Box>
        )}
      </DialogContent>
      <DialogActions sx={{ p: 3, pt: 1 }}>
        <Button onClick={handleSave} disabled={saving} fullWidth sx={{ height: '56px', backgroundColor: '#67314f', borderRadius: '16px', color: 'white', fontWeight: 700, textTransform: 'none', '&:hover': { backgroundColor: '#5a2a42' }, '&:disabled': { backgroundColor: '#93989c' } }}>
          {saving ? <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}><CircularProgress size={20} sx={{ color: 'white' }} /> Saving...</Box> : 'Update Occasions'}
        </Button>
        <Button onClick={onClose} disabled={saving} fullWidth sx={{ height: '56px', backgroundColor: 'transparent', borderRadius: '16px', color: '#67314f', fontWeight: 700, textTransform: 'none', '&:hover': { backgroundColor: 'rgba(103, 49, 79, 0.04)' } }}>
          Cancel
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default OccasionsEditPopup;
